import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api, type AgentStats, type SafeUser } from "../lib/api";
import { cn } from "../lib/cn";
import { IdentityCard } from "../components/IdentityCard";
import { NetworkCard } from "../components/NetworkCard";
import { UsersSection } from "../components/settings/UsersSection";
import { UploadsSection } from "../components/settings/UploadsSection";
import { StorageSection } from "../components/settings/StorageSection";

interface SettingsPageProps {
  user: SafeUser;
}

// SettingsSection is a sidebar entry. The optional `adminOnly` flag mirrors
// the route-level guard — operators don't see Admin entries, the API would
// 403 even if they did.
interface SettingsSection {
  id: string;
  label: string;
  group: "General" | "Admin";
  adminOnly?: boolean;
  hint?: string;
  icon: React.ReactNode;
}

const SECTIONS: SettingsSection[] = [
  {
    id: "identity",
    label: "Plant identity",
    group: "General",
    hint: "Stamped on every artifact",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
        <path
          d="M8 1a3 3 0 1 1 0 6A3 3 0 0 1 8 1Zm-5 9.5a5 5 0 0 1 10 0v.5H3v-.5Z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "network",
    label: "Network",
    group: "General",
    hint: "Interfaces & routes",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3" />
        <path
          d="M8 2c0 0-3 2-3 6s3 6 3 6M8 2c0 0 3 2 3 6s-3 6-3 6M2 8h12"
          stroke="currentColor"
          strokeWidth="1.3"
        />
      </svg>
    ),
  },
  {
    id: "security",
    label: "Security",
    group: "General",
    hint: "Agent certificate",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
        <path
          d="M8 1.5 2.5 4v4c0 3.5 2.5 5.5 5.5 6 3-0.5 5.5-2.5 5.5-6V4L8 1.5Z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "runtime",
    label: "Runtime",
    group: "General",
    hint: "Version & uptime",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3" />
        <path
          d="M8 5v3l2 2"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "users",
    label: "Users",
    group: "Admin",
    adminOnly: true,
    hint: "Roles & access",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
        <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
        <path
          d="M1 13.5a5 5 0 0 1 10 0"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        <path
          d="M12 7v4M10 9h4"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "uploads",
    label: "Uploaded files",
    group: "Admin",
    adminOnly: true,
    hint: "Operator-supplied inputs",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
        <path
          d="M8 10V4M5 7l3-3 3 3"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3 12h10"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    id: "storage",
    label: "Storage",
    group: "Admin",
    adminOnly: true,
    hint: "Disk usage & purge",
    icon: (
      <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
        <ellipse
          cx="8"
          cy="5"
          rx="5"
          ry="2"
          stroke="currentColor"
          strokeWidth="1.3"
        />
        <path
          d="M3 5v3c0 1.1 2.24 2 5 2s5-.9 5-2V5"
          stroke="currentColor"
          strokeWidth="1.3"
        />
        <path
          d="M3 8v3c0 1.1 2.24 2 5 2s5-.9 5-2V8"
          stroke="currentColor"
          strokeWidth="1.3"
        />
      </svg>
    ),
  },
];

const DEFAULT_SECTION = "identity";

// useHashSection wires `window.location.hash` to local state so deep-links
// like /settings#uploads route directly to the right pane and the back/
// forward buttons cycle sections.
function useHashSection(fallback: string): [string, (next: string) => void] {
  const read = () => window.location.hash.replace(/^#/, "").trim() || fallback;
  const [section, setSection] = useState<string>(read);
  useEffect(() => {
    const onHash = () => setSection(read());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const set = (next: string) => {
    if (next !== section) window.location.hash = next;
  };
  return [section, set];
}

// SettingsPage is a 2-column layout: a sticky sub-nav rail on the left and
// the active section on the right. Section is driven by URL hash so
// /settings#users is shareable and the browser back-button does the right
// thing inside the page.
export function SettingsPage({ user }: SettingsPageProps) {
  const [active, setActive] = useHashSection(DEFAULT_SECTION);
  const visible = SECTIONS.filter((s) => !s.adminOnly || user.role === "admin");
  const activeSection = visible.find((s) => s.id === active) ?? visible[0];

  return (
    <div className="page-container">
      {/* HEADER */}
      <header className="pt-8 pb-6">
        <div className="eyebrow mb-1">Configuration</div>
        <h1 className="font-display text-[32px] font-semibold leading-tight tracking-tight text-ink">
          Settings
        </h1>
        <p className="mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-ash">
          Plant identity, network, users, disk usage, and certificates.
        </p>
      </header>

      <div className="flex gap-6">
        {/* SUB-NAV RAIL */}
        <nav className="sticky top-6 w-52 shrink-0 self-start">
          {(["General", "Admin"] as const).map((group) => {
            const items = visible.filter((s) => s.group === group);
            if (items.length === 0) return null;
            return (
              <div key={group} className="mb-4">
                <div className="px-2 pb-1.5 font-mono text-[9.5px] uppercase tracking-[0.18em] text-ash/70">
                  {group}
                </div>
                <ul className="space-y-0.5">
                  {items.map((s) => {
                    const isActive = s.id === activeSection.id;
                    return (
                      <li key={s.id}>
                        <button
                          onClick={() => setActive(s.id)}
                          className={cn(
                            "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors",
                            isActive
                              ? "bg-surface text-ink shadow-navbar ring-1 ring-rule"
                              : "text-datahub-gray-1700 hover:bg-surface hover:text-ink",
                          )}
                        >
                          <span
                            className={cn(
                              "shrink-0",
                              isActive
                                ? "text-litmus-dark-green-700"
                                : "text-ash",
                            )}
                          >
                            {s.icon}
                          </span>
                          <div>
                            <div className="text-[13px] font-medium leading-tight">
                              {s.label}
                            </div>
                            {s.hint && (
                              <div className="text-[11px] text-ash">
                                {s.hint}
                              </div>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* CONTENT PANE */}
        <div className="min-w-0 flex-1 space-y-5">
          {activeSection.id === "identity" && <IdentityCard user={user} />}
          {activeSection.id === "network" && <NetworkCard />}
          {activeSection.id === "security" && <SecuritySection />}
          {activeSection.id === "runtime" && <RuntimeSection />}
          {activeSection.id === "users" && user.role === "admin" && (
            <UsersSection currentUser={user} />
          )}
          {activeSection.id === "uploads" && user.role === "admin" && (
            <UploadsSection />
          )}
          {activeSection.id === "storage" && user.role === "admin" && (
            <StorageSection />
          )}
        </div>
      </div>
    </div>
  );
}

function SecuritySection() {
  return (
    <section className="card p-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="font-display text-[18px] font-semibold tracking-tight text-ink">
            Trust this agent's certificate
          </h2>
          <p className="mt-2 max-w-xl text-[13.5px] leading-relaxed text-ash">
            The agent serves its UI over HTTPS using a self-signed certificate
            generated on first boot. Install it as a trusted root to silence the
            browser warning.
          </p>
        </div>
        <a
          href="/agent.crt"
          download
          className="btn-secondary whitespace-nowrap shrink-0"
        >
          Download agent.crt
        </a>
      </div>
    </section>
  );
}

function RuntimeSection() {
  const stats = useQuery({
    queryKey: ["stats"],
    queryFn: () => api.get<AgentStats>("/api/v1/stats"),
  });
  const data = stats.data;
  return (
    <section className="card overflow-hidden">
      <div className="border-b border-rule px-5 py-4">
        <h2 className="font-display text-[17px] font-semibold tracking-tight text-ink">
          Runtime
        </h2>
        <p className="mt-0.5 text-[13px] text-ash">
          Build version and boot time. Useful when reporting an issue.
        </p>
      </div>
      <dl className="divide-y divide-rule">
        <Row label="Agent version" value={data?.agentVersion ?? "—"} mono />
        <Row
          label="Connectors"
          value={`${data?.connectorsRegistered ?? 0} registered`}
        />
        <Row
          label="Booted"
          value={
            data?.startedAt ? new Date(data.startedAt).toLocaleString() : "—"
          }
        />
      </dl>
    </section>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-4 px-5 py-3">
      <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-ash">
        {label}
      </dt>
      <dd className={cn("text-ink", mono && "font-mono text-[12.5px]")}>
        {value}
      </dd>
    </div>
  );
}
